import { type ChangeEvent, forwardRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import InputBase from '@mui/material/InputBase';
import Slide, { type SlideProps } from '@mui/material/Slide';
import { ACCOUNT_TYPES } from '@witmetrics/utils/constants';
import type {
  DecoratedUserPractice,
  UndecoratedPractice,
} from '@witmetrics/utils';
import {
  useActiveAccount,
  useCurrentUser,
  useDispatch,
  useUserPractices,
} from '@witmetrics/data-store';
import { sortAlphabeticalByKey } from '@witmetrics/utils/arrays';
import { containsString, isBlank } from '@witmetrics/utils/strings';
import { useAppState } from '@/providers/AppStateProvider';
import { API } from '@/api';
import PracticesList from './PracticesList';
import { classes, styles } from './utils';

const { PRACTICE } = ACCOUNT_TYPES;

const Transition = forwardRef<any, SlideProps>((props, ref) => {
  return <Slide ref={ref} direction="down" {...props} />;
});

export type AccountsDialogProps = {
  onClose: () => void;
};

export default function AccountsDialog({ onClose }: AccountsDialogProps) {
  const dispatch = useDispatch();
  const { onApiError } = useAppState();
  const currentUser = useCurrentUser();
  const userPractices = useUserPractices(currentUser!.id);
  const activeAccount = useActiveAccount();
  const [search, setSearch] = useState('');

  const handleClose = () => {
    // Stop dialog from closing when there's no active account
    if (activeAccount && activeAccount.accountType === PRACTICE) onClose();
  };

  const getPractices = () => {
    let practices: UndecoratedPractice[] = userPractices.map(
      (up: DecoratedUserPractice) => up.practice
    );
    if (!isBlank(search)) {
      practices = practices.filter((p) => containsString(p.name, search));
    }
    return sortAlphabeticalByKey<UndecoratedPractice>(practices, 'name');
  };

  const handleSearch = (
    e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    setSearch(e.target.value);
  };

  const handleSelect = async (practiceId: number) => {
    try {
      if (
        activeAccount?.accountType === PRACTICE &&
        practiceId === activeAccount?.accountId
      ) {
        return onClose();
      }
      const result = await API.Users.setActiveAccount(PRACTICE, practiceId);
      dispatch.addActiveAccount(result.activeAccount);
      onClose();
    } catch (err) {
      onApiError(err, 'Error setting active account');
    }
  };

  return (
    <Dialog
      open
      keepMounted
      disableRestoreFocus
      maxWidth={false}
      sx={styles.dialog}
      PaperProps={{ className: classes.paper }}
      TransitionComponent={Transition}
      onClose={handleClose}>
      <div className={classes.wrapper}>
        <InputBase
          autoFocus
          className={classes.inputBase}
          autoComplete="off"
          name="accounts-search"
          placeholder="Search for an account..."
          type="text"
          value={search}
          onChange={handleSearch}
        />
        <PracticesList
          className={classes.resultsList}
          practices={getPractices()}
          onSelect={handleSelect}
        />
      </div>
    </Dialog>
  );
}
